<template>
  <Dialog :visible.sync="visible" modal :header="form.id ? 'Editar Stock' : 'Adicionar Stock'" :style="{ width: '400px' }" @hide="$emit('close')">
    <form @submit.prevent="submit" ref="form">
      <div class="p-fluid">
        <div class="p-field">
          <label for="product_reference">Referência do Produto</label>
          <AutoComplete
              label="Referência do Produto"
              v-model="product_picked"
              @complete="searchProduct"
              forceSelection
              :suggestions="productSuggestions"
              @item-select="(e) => onProductSelect()"
              field="code"
              placeholder="Pesquise produto"
              style="width: 100%"
              :dropdown="true"
              appendTo="body"
              v-validate="'required'"
              id="product_reference"
              name="product_reference"
              :class="{ 'p-invalid': errors.has('product_reference') }"
          >
              <template #item="itemSlot">
                  <div>
                      <strong>{{ itemSlot.item.code }}</strong> - {{ itemSlot.item.description }}
                  </div>
              </template>
          </AutoComplete>
          <span v-show="errors.has('product_reference')" class="p-error">{{ errors.first('product_reference') }}</span>
        </div>

        <div class="p-field">
          <label for="storage_id">Armazém</label>
          <Dropdown
            id="storage_id"
            v-model="form.storage_id"
            name="storage_id"
            v-validate="'required'"
            :class="{ 'p-invalid': errors.has('storage_id') }"
            :options="storages"
            optionLabel="nome"
            optionValue="no"
            placeholder="Selecione o armazém"
            class="p-dropdown-sm"
            appendTo="body"
          />
          <span v-show="errors.has('storage_id')" class="p-error">{{ errors.first('storage_id') }}</span>
        </div>

        <div class="p-field">
          <label for="current_quantity">Quantidade Atual</label>
          <InputText
            id="current_quantity"
            v-model="form.current_quantity"
            name="current_quantity"
            type="number"
            v-validate="'required|numeric|min_value:0'"
            :class="{ 'p-invalid': errors.has('current_quantity') }"
          />
          <span v-show="errors.has('current_quantity')" class="p-error">{{ errors.first('current_quantity') }}</span>
        </div>

        <div class="p-field">
          <label for="optimal_quantity">Stock Ótimo</label>
          <InputText
            id="optimal_quantity"
            v-model="form.optimal_quantity"
            name="optimal_quantity"
            type="number"
            v-validate="'required|numeric|min_value:0'"
            :class="{ 'p-invalid': errors.has('optimal_quantity') }"
          />
          <span v-show="errors.has('optimal_quantity')" class="p-error">{{ errors.first('optimal_quantity') }}</span>
        </div>

        <div class="p-field">
          <label for="minimum_quantity">Stock Mínimo</label>
          <InputText
            id="minimum_quantity"
            v-model="form.minimum_quantity"
            name="minimum_quantity"
            type="number"
            v-validate="'required|numeric|min_value:0'"
            :class="{ 'p-invalid': errors.has('minimum_quantity') }"
          />
          <span v-show="errors.has('minimum_quantity')" class="p-error">{{ errors.first('minimum_quantity') }}</span>
        </div>

        <div class="p-field">
          <label for="consumption">Consumo</label>
          <InputText
            id="consumption"
            v-model="form.consumption"
            name="consumption"
            type="number"
            v-validate="'required|numeric|min_value:0'"
            :class="{ 'p-invalid': errors.has('consumption') }"
          />
          <span v-show="errors.has('consumption')" class="p-error">{{ errors.first('consumption') }}</span>
        </div>

        <div class="p-field">
          <label for="selling_price">Preço Venda</label>
          <InputNumber
            v-model="form.selling_price"
            mode="currency"
            currency="EUR"
            locale="pt-PT"
            :minFractionDigits="2"
            :maxFractionDigits="6"
            style="width: 100%"
            id="selling_price"
            name="selling_price"
            :class="{ 'p-invalid': errors.has('selling_price') }"
          />
          <span v-show="errors.has('selling_price')" class="p-error">{{ errors.first('selling_price') }}</span>
        </div>
      </div>
    </form>

    <template #footer>
      <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="$emit('close')" />
      <Button label="Guardar" icon="pi pi-check" @click="submit" />
    </template>
  </Dialog>
</template>

<script>
import StockService from '@/services/stock.service';

export default {
  name: 'StockModal',
  props: {
    visible: Boolean,
    data: Object,
    storages: Array,
  },
  data() {
    return {
      productSuggestions: [],
      product_picked: null,
      form: {
        id: null,
        product_reference: '',
        storage_id: '',
        current_quantity: 0,
        optimal_quantity: 0,
        minimum_quantity: 0,
        consumption: 0,
        selling_price: 0,
      }
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.errors.clear();
          this.$validator.reset();
        });
      }
    },
    data: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.form = {
            id: newVal.id ?? null,
            product_reference: newVal.product_reference ?? '',
            storage_id: newVal.storage_id ?? '',
            current_quantity: newVal.current_quantity ?? 0,
            optimal_quantity: newVal.optimal_quantity ?? 0,
            minimum_quantity: newVal.minimum_quantity ?? 0,
            consumption: newVal.consumption ?? 0,
            selling_price: newVal.selling_price ?? 0,
          };

          if (newVal.product_reference) {
            this.product_picked = {
              code: newVal.product_reference ?? '',
            };
          } else {
            this.product_picked = null;
          }
        } else {
          this.reset();
        }
      }
    }
  },
  methods: {
    reset() {
      this.form = {
        id: null,
        product_reference: '',
        storage_id: '',
        current_quantity: 0,
        optimal_quantity: 0,
        minimum_quantity: 0,
        consumption: 0,
        selling_price: 0,
      };

      this.product_picked = null;
    },
    onProductSelect() {
      this.form.product_reference = this.product_picked ? this.product_picked.code : '';
    },
    async searchProduct(event) {
      try {
        const query = event.query;
        const response = await StockService.getQueryProducts({ search: query });
        this.productSuggestions = response.data.data;
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        this.productSuggestions = [];
      }
    },
    async submit() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) {
        const firstError = this.$el.querySelector('.p-error');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      try {
        const payload = { ...this.form };

        if (this.form.id) {
          let trimID = this.form.id.trim();
          await StockService.update(trimID, payload);
        } else {
          await StockService.create(payload);
        }

        this.$emit('saved');
        this.reset();
      } catch (error) {
        console.error('Erro ao guardar stock', error);
      }
    },
  }
};
</script>
